import { AwsRum, AwsRumConfig } from 'aws-rum-web';
import config from './config';

let rumClient: AwsRum | null = null;

/**
 * Errors we don't send to RUM: non-actionable or noisy so they don't clutter metrics.
 * Add new conditions here as needed.
 */
function shouldIgnoreRumError(event: ErrorEvent | PromiseRejectionEvent): boolean {
  if (event instanceof ErrorEvent) {
    // Cross-origin script error: "Script error." with no location (third-party / in-app browser)
    if (
      event.message === 'Script error.' &&
      !event.filename &&
      (event.lineno === 0 || event.lineno == null) &&
      (event.colno === 0 || event.colno == null)
    ) {
      return true;
    }
    // Facebook in-app browser autofill bridge bug (injected script, not our code)
    const stack = event.error?.stack ?? '';
    if (typeof stack === 'string' && stack.includes('setContactAutofillValuesFromBridge')) {
      return true;
    }
    return false;
  }
  if (event instanceof PromiseRejectionEvent) {
    const stack = event.reason?.stack ?? '';
    if (typeof stack === 'string' && stack.includes('setContactAutofillValuesFromBridge')) {
      return true;
    }
    return false;
  }
  return false;
}

/**
 * Initialize AWS CloudWatch RUM when REACT_APP_RUM_ENABLED is true and
 * REACT_APP_RUM_APP_MONITOR_ID and REACT_APP_RUM_IDENTITY_POOL_ID are set.
 * Call this once at app startup (e.g. in index.tsx).
 */
export function initRum(): void {
  if (!config.app.is_rum_enabled || !config.app.rum_app_monitor_id || !config.app.rum_identity_pool_id) {
    return;
  }

  const region = config.apiGateway.REGION;
  const rumConfig: AwsRumConfig = {
    sessionSampleRate: 1,
    identityPoolId: config.app.rum_identity_pool_id,
    endpoint: `https://dataplane.rum.${region}.amazonaws.com`,
    telemetries: [['errors', { ignore: shouldIgnoreRumError }], 'http', 'performance'],
    allowCookies: true,
  };

  try {
    rumClient = new AwsRum(
      config.app.rum_app_monitor_id,
      '1.0.0', // application version; can be driven by env if needed
      region,
      rumConfig,
    );
  } catch (e) {
    console.warn('[RUM] Failed to initialize AWS RUM:', e);
  }
}

/**
 * Record a custom page view for RUM (e.g. on route change).
 * Use the pathname (or a normalized path) so pages aggregate correctly.
 */
export function recordRumPageView(pageId: string): void {
  if (!rumClient) return;
  try {
    rumClient.recordPageView(pageId);
  } catch {
    // no-op if RUM not initialized or recording fails
  }
}

/**
 * Record an error in RUM (e.g. from an error boundary).
 */
export function recordRumError(error: unknown): void {
  if (!rumClient) return;
  try {
    rumClient.recordError(error instanceof Error ? error : new Error(String(error)));
  } catch {
    // no-op
  }
}
